import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import type { RoleCreateInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { RolesDialogContent } from "@/screens/SystemAdmin/components/dialogs"
import { RoleForm } from "@/screens/SystemAdmin/components/forms"
import { useRole } from "@/screens/SystemAdmin/hooks"
import { RoleFormSchema } from "@/screens/SystemAdmin/schema"
import { SystemAdminDialog } from "@/screens/SystemAdmin/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddNewRoleDialogProps {
  isOpen: SystemAdminDialog | null
  onClose: () => void
  onSuccess: () => void
}

const AddNewRoleDialogProps: FC<AddNewRoleDialogProps> = ({ isOpen, onClose, onSuccess }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createRole } = useRole()

  const methods = useForm<RoleCreateInput>({
    defaultValues: {
      name: "",
      permissionIds: [],
    },
    mode: "all",
    resolver: zodResolver(RoleFormSchema),
  })

  const {
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = methods

  const handleSubmitData = async (fields: RoleCreateInput) => {
    const { error, data } = await createRole({
      input: fields,
    })
    const hasError =
      error?.message || (data?.role.create.__typename === "RoleFailure" && Boolean(data?.role.create.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create role",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Role successfully created",
    })
    onSuccess()
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <Dialog open={isOpen === SystemAdminDialog.ADD_NEW_ROLE} onClose={handleCancel} fullWidth>
      <RolesDialogContent
        isSubmitDisabled={!isValid || isSubmitting}
        title='Add New Role'
        handleCancel={handleCancel}
        handleSave={handleSubmit(handleSubmitData)}
      >
        <FormProvider {...methods}>
          <RoleForm isEditMode={false} />
        </FormProvider>
      </RolesDialogContent>
    </Dialog>
  )
}

export default AddNewRoleDialogProps
